// Small helpers for authoring flows (`ItemDefinition` collections).
//
// These are entirely optional sugar over the raw `ItemDefinition` API — they
// exist to cut the most common boilerplate out of `chooseNext` and to remove
// the magic `'theEnd'` string. See `docs/authoring/README.md` for the full
// walkthrough and `docs/authoring/example_items.js` for a worked example.

import { ItemDefinition } from './utils';

// The engine treats the item whose id is `END` as the terminal node: reaching
// it finishes the flow. Every flow needs exactly one such item.
export const END = 'theEnd';

// Build the terminal item. Give it a title (shown on the final screen) and,
// optionally, extra `ItemDefinition` attributes (e.g. a `markdown` field).
export function endItem(title = 'Done', attributes = {}) {
  return new ItemDefinition({
    id: END,
    title,
    fields: [],
    chooseNext: goTo(END),
    ...attributes,
  });
}

// `chooseNext` for a straight-line step. Pass the id of the next item:
//   chooseNext: goTo('next_item')
// `chooseNext` may return an item id (resolved by the engine) or an
// `ItemDefinition`; returning the id string avoids JS declaration-order issues
// when items reference each other.
export function goTo(nextItemOrId) {
  return function () {
    return nextItemOrId;
  };
}

// `chooseNext` for a yes/no branch. `fieldIndex` is the position of the
// `yesno` field in the item's `fields`; a "yes" answer is stored as `1`.
//   chooseNext: branchOnYesNo(0, 'yes_item', 'no_item')
export function branchOnYesNo(fieldIndex, yesItemOrId, noItemOrId) {
  return function (states) {
    const { values } = states[this.id];
    return values[fieldIndex] === 1 ? yesItemOrId : noItemOrId;
  };
}

// `chooseNext` for the last item of a loop: keep iterating until the loop has
// run `count` times, then continue to `afterItemOrId`. `count` is either a
// number or a function `(states, globals, loops) => number` (typically reading
// a "how many?" answer from an earlier item).
//   chooseNext: loopUntil((states) => states['how_many'].values[0], 'after_loop')
export function loopUntil(count, afterItemOrId) {
  return function (states, globals, loops) {
    const total = typeof count === 'function' ? count.call(this, states, globals, loops) : count;
    if (this.getLoopIndex(loops) >= total) {
      return afterItemOrId;
    }
    return this.nextLoopIteration(loops);
  };
}
